/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {ApiConfig} from '../../api';
import {AdnHttpService} from '../../../util/adn-http.service';
import {
  createAdnHttpResource,
  createAdnHttpResourceAction,
  createAdnHttpResourceSender
} from '../../../util/create-adn-http-resource';
import {filterUrlLessTargeting} from '../../../util/targeting-request';
import {LocalUserProfile} from '../../../session/local-user-profile';
import {AssetService} from './asset/asset.service';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';

export function creativeFunctions(Creative: any, Asset: any): void {
  Creative.create = function(defaults: any) {
    return new Creative(_.assign({
      assets: []
    }, defaults));
  };

  Creative.hydrate = function(creative: any) {
    if (!creative) {
      return Promise.resolve(creative);
    }
    const promises = [];
    promises.push(Asset.fromCreative(creative.id).then(function(assets: any) {
      creative.assets = assets;
    }));
    const libraryCreativeId = _.get(creative.libraryCreative, 'id', '');
    if (libraryCreativeId) {
      promises.push(Asset.fromCreative(libraryCreativeId).then(function(assets: any) {
        creative.libraryCreativeAssets = assets;
      }));
    }
    return Promise.all(promises).then(function() {
      return creative;
    });
  };

  Creative.getHydrated = function(params: any) {
    return Creative.get(params).$promise.then(function(creative: any) {
      return Creative.hydrate(creative);
    });
  };

  Creative.getHydratedPreview = function(creativeId: any) {
    return Creative.get({id: creativeId}).$promise.then(function(creative: any) {
      const assetIds = _.map(_.filter(creative.layoutParameters, function(v: any) {
        return v.url;
      }), function(val: any) {
        return val.id;
      });
      if (assetIds.length < 1) {
        return creative;
      }
      return Asset.fromCreative(creativeId, assetIds).then(function(assets: any) {
        creative.assets = assets;
        return creative;
      });
    });
  };

  Creative.copyCreative = function(creative: any, savedLineItem: any, newId: any, userState: any, savedCreativeSet: any, transcodeConfig: any, dimensions: any, objectState: any) {
    if (!creative) {
      return Promise.reject('Insufficient parameters for the creative copy');
    }
    return Creative.copy({
      id: _.isString(creative) ? creative : creative.id,
      creativeSetId: _.isString(savedCreativeSet) ? savedCreativeSet : _.get(savedCreativeSet, ['id']),
      lineItemId: _.isString(savedLineItem) ? savedLineItem : _.get(savedLineItem, ['id']),
      userState: userState || '',
      objectState: objectState || null,
      creativeId: newId || '',
      name: creative.name,
      transcodeConfig: transcodeConfig ? JSON.stringify(transcodeConfig) : '',
      dimensions: dimensions ? dimensions : null
    }).$promise;
  };

  Creative.retailAd = function(lineItemId: any, creativeId: any, layoutId: any, merchant: any, item: any) {
    return Creative.retail({lineItem: lineItemId, creative: creativeId, layout: layoutId, merchant: merchant, item: item}).$promise;
  };
}

/** Angular DI token for the Creative HTTP resource (factory product). */
export class CreativeResource {
}

export function createCreativeResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile,
  Asset: any
): any {
  const transformRequest = function(creative: any) {
    filterUrlLessTargeting(creative);
    if (!_.get(creative, ['lineItem', 'id'])) {
      delete creative.lineItem;
    }

    if (!_.get(creative, ['creativeSet', 'id'])) {
      delete creative.creativeSet;
    }
    return creative;
  };
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const Creative = createAdnHttpResource(
    send,
    'creatives',
    () => localUserProfile,
    undefined,
    undefined,
    {transformRequest}
  );
  const uri = ApiConfig.obtain().getVersionedUri('creatives');
  const CreativeInfo = createAdnHttpResource(
    send,
    'creatives',
    () => localUserProfile,
    undefined,
    undefined,
    {uri: uri + '/info'}
  );

  Creative.info = CreativeInfo.query;
  Creative.copy = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/:id/copy',
    queryParams: ['lineItemId', 'creativeSetId', 'creativeId', 'name', 'userState', 'transcodeConfig', 'dimensions', 'objectState']
  });
  Creative.retail = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/retail',
    queryParams: ['lineItemId', 'merchant', 'item', 'creativeId', 'layoutId']
  });

  creativeFunctions(Creative, Asset);

  return Creative;
}

export const creativeResourceProvider = {
  provide: CreativeResource,
  useFactory: createCreativeResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile, AssetService]
};

